import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import type { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import type { TButtonModifier } from '@frontend/pole-ui/lib/components/Button'
import { ShevronDownIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronDownIcon'
import { ShevronRightIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronRightIcon'
import { ShevronUpIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronUpIcon'
import classnames from 'classnames'
import { Button, Image } from '@/components/ui'
import { JOURNAL_TAG_ATTRIBUTES } from '@/constants/journal'
import type { FINANCING_WIDGETS } from '@/pages/JournalPublication/components/PublicationText/constants'
import type { EFinancingOfferType } from '@/types'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import type { IPublicationComponentProps } from '../types'
import { PublicationFinancingWidgetCollapsingAgentRegistration } from './components/PublicationFinancingWidgetCollapsingAgentRegistration'
import { PublicationFinancingWidgetCollapsingFinancingProductOrder } from './components/PublicationFinancingWidgetCollapsingFinancingProductOrder'
import { MORE_LINKS } from './constants'
import { messages } from './messages'
import './style.scss'

const { financingWidget } = JOURNAL_TAG_ATTRIBUTES

export const PublicationFinancingWidget: React.FC<IPublicationComponentProps> = props => {
  const { attribs } = props
  const [isShowCollapsingBlock, setIsShowCollapsingBlock] = useState(false)

  const history = useHistory()
  const { isMobile } = useDeviceType()

  const widgetType = attribs[financingWidget.type] as keyof typeof FINANCING_WIDGETS
  const financingProductId = attribs[financingWidget.financingProductId] as FundingProduct
  const financingOfferType = attribs[financingWidget.financingOfferType] as EFinancingOfferType

  const imageName = attribs[financingWidget.type]?.toLowerCase()

  const renderCollapsingBlock = () => {
    if (widgetType === 'AGENT') return <PublicationFinancingWidgetCollapsingAgentRegistration />

    return (
      <PublicationFinancingWidgetCollapsingFinancingProductOrder
        widgetType={widgetType}
        financingProductId={financingProductId}
        financingOfferType={financingOfferType}
      />
    )
  }

  const goToMore = () => {
    history.push(MORE_LINKS[widgetType])
  }

  const linkModificators = (): TButtonModifier[] => {
    if (isMobile) return ['outline-gray', 'full-width-mobile']

    return ['transparent']
  }

  return (
    <div className="publicationFinancingWidget">
      <div
        className={classnames('publicationFinancingWidgetBanner', {
          'publicationFinancingWidgetBanner--active': isShowCollapsingBlock,
        })}
      >
        <div className="publicationFinancingWidgetBanner__text">
          <h3 className="h3 publicationFinancingWidgetBanner__title">{attribs[financingWidget.title]}</h3>
          <div className="publicationFinancingWidgetBanner__description">{attribs[financingWidget.description]}</div>

          <div className="publicationFinancingWidgetBanner__actions">
            <Button
              modifiers={['green', 'full-width-mobile']}
              onClick={() => setIsShowCollapsingBlock(!isShowCollapsingBlock)}
              iconProps={{
                component: isShowCollapsingBlock ? <ShevronUpIcon /> : <ShevronDownIcon />,
              }}
              iconPosition="right"
            >
              {attribs[financingWidget.buttonText]}
            </Button>
            <Button
              className="publicationFinancingWidgetBanner__link"
              modifiers={linkModificators()}
              iconProps={{ component: <ShevronRightIcon /> }}
              iconPosition="right"
              onClick={goToMore}
            >
              {messages.more}
            </Button>
          </div>
        </div>
        <div className="publicationFinancingWidgetBanner__illustration">
          <Image className="publicationFinancingWidgetBanner__image" src={`/publicationFinancingWidget/${imageName}.png`} isInStatic />
        </div>
      </div>

      {isShowCollapsingBlock && renderCollapsingBlock()}
    </div>
  )
}
